import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { PixelHealth } from '@/types/pixel';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PixelHealthBadgeProps {
  health?: PixelHealth;
}

function formatAgo(dateStr: string): string {
  try {
    const diffMin = Math.max(0, Math.round((Date.now() - new Date(dateStr).getTime()) / 60000));
    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `há ${diffMin} min`;
    const h = Math.round(diffMin / 60);
    if (h < 24) return `há ${h}h`;
    return `há ${Math.round(h / 24)}d`;
  } catch {
    return '—';
  }
}

const EVENT_LABELS: Record<string, string> = {
  // contrato padrão (todas as plataformas)
  pageView: 'page view',
  videoClick: 'clique no play',
  get videoRetention() { return translateInterfaceText("retenção"); },
  get videoDuration() { return translateInterfaceText("duração"); },
  // legados/internal (Meta CAPI)
  play: 'play',
  get pitch() { return translateInterfaceText("oferta visível"); },
  checkout: 'clique no checkout',
  cta_click: 'clique no checkout',
  view: 'view',
  video_click: 'play clicado',
  hostvsl_play: 'play',
  hostvsl_view: 'view',
  hostvsl_video_click: 'play clicado',
};

/**
 * Selo de saúde do pixel baseado em EVIDÊNCIA real de disparo, registrada
 * server-side em tb_pixel_fired (Meta CAPI e TikTok Events API; nenhum beacon
 * no player — padrão da casa desde 2026-09-21): "Registrando" quando houve
 * disparo aceito nas últimas 24h, "Mudo há +24h" quando já registrou mas
 * parou, "Aguardando 1º disparo" quando nunca saiu nada. Não confunde com o
 * status da integração (Ativo/Desativado), que é a coluna do lado.
 */
export function PixelHealthBadge({ health }: PixelHealthBadgeProps) {
  useLanguage();
  if (!health || !health.total) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-[#94A3B8]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#94A3B8]" />
              {translateInterfaceText("Aguardando 1º disparo")}</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px]">
            <p>{translateInterfaceText("Nenhum disparo registrado ainda. Abra o VSL uma vez com som e recarregue esta tela. Se já abriu e nada aparece, confira se a URL vinculada é a página onde o vídeo roda.")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (health.last24h > 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5 text-[11px] text-[#16A34A]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" />
              {translateInterfaceText("Registrando ·")}{" "}{formatAgo(health.last_at || '')}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px]">
            <p>
              {health.last24h} {" "}{translateInterfaceText("evento(s) nas últimas 24h (")}{health.total} {" "}{translateInterfaceText("no total).")}{health.last_event ? translateInterfaceText(" Último: {value0}.", { value0: (EVENT_LABELS[health.last_event] || health.last_event) }) : ''}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-[#D97706]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D97706]" />
            {translateInterfaceText("Mudo há +24h")}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[280px]">
          <p>
            {translateInterfaceText("Já registrou")}{" "}{health.total} {" "}{translateInterfaceText("evento(s), mas nenhum nas últimas 24h (último:")}{" "}{formatAgo(health.last_at || '')}{translateInterfaceText("). Ou o VSL não foi aberto nas últimas 24h, ou o envio server-side falhou (credencial/endpoint) — ou a URL vinculada não é a página do vídeo.")}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
